/**
 * Format date to human-readable format
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} - Formatted date string
 */
export const formatInterviewDate = (dateInput) => {
  if (!dateInput) return null;

  try {
    const date = new Date(dateInput);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }

    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC", // Adjust based on your requirements
    };

    return date.toLocaleDateString("en-US", options);
  } catch (error) {
    console.error("Date formatting error:", error);
    return null;
  }
};

/**
 * Format date to a more specific format like "9.00 pm, 12 August 2025"
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} - Formatted date string
 */
export const formatInterviewDateTime = (dateInput) => {
  if (!dateInput) return null;

  try {
    const date = new Date(dateInput);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }

    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const dateOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };

    const time = date.toLocaleTimeString("en-US", timeOptions);
    const formattedDate = date.toLocaleDateString("en-US", dateOptions);

    return `${time}, ${formattedDate}`;
  } catch (error) {
    console.error("Date formatting error:", error);
    return null;
  }
};

/**
 * Get default fallback date
 * @returns {string} - Default date string
 */
export const getDefaultInterviewDate = () => {
  return "9.00 am, 12 August 2025";
};

/**
 * Check if date is in the future
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {boolean} - True if date is in future
 */
export const isDateInFuture = (dateInput) => {
  if (!dateInput) return false;

  try {
    const date = new Date(dateInput);
    const now = new Date();

    return date > now;
  } catch (error) {
    console.error("Date comparison error:", error);
    return false;
  }
};

/**
 * Get time remaining until interview date
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} - Time remaining string
 */
export const getTimeRemaining = (dateInput) => {
  if (!dateInput) return null;

  try {
    const targetDate = new Date(dateInput);
    const now = new Date();
    const difference = targetDate - now;

    if (difference <= 0) {
      return "Interview date has passed";
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""} remaining`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""} remaining`;
    } else {
      return `${minutes} minute${minutes > 1 ? "s" : ""} remaining`;
    }
  } catch (error) {
    console.error("Time calculation error:", error);
    return null;
  }
};
export const calculateAge = (dob) => {
  if (!dob) return 0;
  const today = new Date();
  const birthDate = new Date(dob);

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};
